<template>
  <BasePageTemplate :title="loginTitleString" :description="loginDescriptionString" v-model:error="error">
    <DynamicForm v-model:data="data" :definition="formDefinition" :form-id="formId">
      <!-- Username field -->
      <Field :label="usernameString" is-required target="username">
        <IconField>
          <InputIcon>
            <OutlinedIcon icon="person" class="text-xl" />
          </InputIcon>

          <InputText
            :ref="usernameInputId"
            v-model="data.username"
            fluid
            :disabled="disabled"
            :pt="{
              root: {
                autoComplete: 'username',
              },
            }"
            @enter="onNextClickAsync"
          />
        </IconField>
      </Field>

      <MainButton :label="nextString" :disabled="disabled" :loading="disabled && !isLoginWithPasskey" @click="onNextClickAsync" />

      <Button
        v-if="globalConfigStore.totpEnabled"
        :label="signInWithPasskeyString"
        :disabled="disabled || isLoginWithPasskey"
        :loading="isLoginWithPasskey"
        @click="onSignInWithPasskeyClickAsync"
      ></Button>
    </DynamicForm>

    <!-- Registration buttons -->
    <div v-if="globalConfigStore.allowRegistration" class="flex items-center justify-center text-sm gap-2">
      <span class="leading-[150%]">{{ registerLabelString }}</span>
      <Button class="p-0 leading-[150%]" :label="registerString" link :disabled="disabled" @click="onRegistrationClickAsync" />
    </div>
  </BasePageTemplate>
</template>

<script setup lang="ts">
import { AuthHttpHeader, Endpoints } from "@authentication/constants";
import { useRedirectionTools } from "@authentication/hooks";
import { AuthenticationApiService } from "@authentication/services";
import type { SignInPreferencesType, SignInType } from "@authentication/types";
import { BasePageTemplate } from "@authentication/ui";
import type { IFormDefinition } from "@common/core";
import { useForm, useProblemDetailsUtils } from "@common/hooks";
import type { ProblemDetailsType } from "@common/types";
import { DynamicForm } from "@common/ui";
import { Button } from "@common/ui/atoms/Buttons/Button";
import { MainButton } from "@common/ui/atoms/Buttons/MainButton";
import { Field } from "@common/ui/atoms/Form/Field";
// biome-ignore lint/style/useImportType:
import { InputText } from "@common/ui/atoms/Form/InputText";
import { OutlinedIcon } from "@common/ui/atoms/Icons/OutlinedIcon";
import { client } from "@passwordless-id/webauthn";
import type { AuthenticationJSON } from "@passwordless-id/webauthn/dist/esm/types";
import { useGlobalConfigStore } from "@shared/stores/useGlobalConfigStore.ts";
import { HttpStatusCode } from "axios";
import { useTranslation } from "i18next-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import * as yup from "yup";

// Models
const data = defineModel<SignInType>("data", { required: true });

// Emits
const emit = defineEmits<{
  (e: "next", preferences: SignInPreferencesType): void;
  (e: "signedIn", redirectTo: string): void;
}>();

// Constants
const formId = useId();
const usernameInputId = useId();

// Variables
const { t } = useTranslation();
const problemDetailUtils = useProblemDetailsUtils();
const redirectionTools = useRedirectionTools();
const globalConfigStore = useGlobalConfigStore();
const dataFrom = useForm([formId]);
const usernameInput = useTemplateRef<InstanceType<typeof InputText>>(usernameInputId);
const disabled = ref(false);
const isLoginWithPasskey = ref(false);
const error = ref<ProblemDetailsType | null>();

// Strings
const nextString = t("commons.next");
const badRequestString = t("login.bad_request");
const loginTitleString = t("login.form_title");
const loginDescriptionString = t("login.form_description");
const registerString = t("login.register");
const registerLabelString = t("login.register_label");
const signInWithPasskeyString = t("login.sing_in_with_passkey");
const signInWithPasskeyErrorString = t("login.sign_in_with_passkey_error");
const usernameString = t("identity_server_application.username");
const userNotFoundString = t("identity_server_application.user_not_found");

// Forms
const formDefinition = {
  username: {
    spec: yup.string().required().min(3).label(usernameString),
  },
} as IFormDefinition;

// Methods
const onNextClickAsync = async () => {
  disabled.value = true;

  dataFrom.cleanErrors();

  if (!(await dataFrom.isValidAsync())) {
    disabled.value = false;

    await nextTick(() => {
      usernameInput.value?.$el.focus();
    });

    return;
  }

  AuthenticationApiService.initSignInAsync(data.value.username)
    .then((response) => {
      emit("next", response);
    })
    .catch((e: ProblemDetailsType) => {
      if (e.status === HttpStatusCode.NotFound) {
        setErrorMessage(userNotFoundString);
      } else if (e.status === HttpStatusCode.BadRequest) {
        setErrorMessage(badRequestString);
      } else {
        error.value = e;
      }
    })
    .finally(() => {
      disabled.value = false;
    });
};

const onRegistrationClickAsync = async () => {
  await redirectionTools.redirectToAsync(Endpoints.pageRegistrationPath);
};

const onSignInWithPasskeyClickAsync = async () => {
  let challenge = "";
  let response: AuthenticationJSON;

  disabled.value = true;
  isLoginWithPasskey.value = true;

  try {
    challenge = await AuthenticationApiService.initSignInWithPasskeyAsync();
  } catch (e) {
    error.value = e as ProblemDetailsType;
    disabled.value = false;
    isLoginWithPasskey.value = false;
    return;
  }

  try {
    response = await client.authenticate({
      challenge: challenge,
      userVerification: "required",
      timeout: 6000,
    });
  } catch {
    disabled.value = false;
    isLoginWithPasskey.value = false;
    return;
  }

  AuthenticationApiService.signInWithPasskeyAsync(response)
    .then((response) => {
      const redirectUri = response.headers[AuthHttpHeader.XLocation];

      emit("signedIn", redirectUri);
    })
    .catch((_) => {
      setErrorMessage(signInWithPasskeyErrorString);
      disabled.value = false;
      isLoginWithPasskey.value = false;
    });
};

const setErrorMessage = (message: string) => {
  error.value = problemDetailUtils.fromString(message);
};

// Hooks
onMounted(async () => {
  await nextTick(() => {
    usernameInput.value?.$el.focus();
  });
});
</script>
